import { useState, useEffect, useCallback } from "react";
import {
  SystemParameter,
  getSystemParameters,
  updateParametersBulk,
  createParameter,
  deleteParameter,
  ParameterInput,
} from "../../services/parameter.service";
import { message } from "antd";

export default function useParameters() {
  const [parameters, setParameters] = useState<SystemParameter[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);

  const fetchParameters = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getSystemParameters();
      setParameters(data);
    } catch (error) {
      console.error("Error fetching parameters", error);
      message.error("No se pudieron cargar los parámetros del sistema.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchParameters();
  }, [fetchParameters]);

  const saveParameters = async (params: ParameterInput[]) => {
    setSaving(true);
    try {
      await updateParametersBulk(params);
      message.success("Parámetros actualizados con éxito");
      await fetchParameters(); // Reload to confirm changes
    } catch (error) {
      console.error("Error saving parameters", error);
      message.error("Hubo un error al guardar los parámetros.");
    } finally {
      setSaving(false);
    }
  };

  const addParameter = async (param: ParameterInput) => {
    setSaving(true);
    try {
      await createParameter(param);
      message.success(`Parámetro "${param.key}" creado con éxito`);
      await fetchParameters();
    } catch (error) {
      console.error("Error creating parameter", error);
      message.error("Hubo un error al crear el parámetro.");
    } finally {
      setSaving(false);
    }
  };

  const removeParameter = async (key: string) => {
    setSaving(true);
    try {
      await deleteParameter(key);
      message.success(`Parámetro "${key}" eliminado`);
      await fetchParameters();
    } catch (error) {
      console.error("Error deleting parameter", error);
      message.error("Hubo un error al eliminar el parámetro.");
    } finally {
      setSaving(false);
    }
  };

  /** Group parameters by category */
  const getByCategory = (category: string): SystemParameter[] => {
    return parameters.filter((p) => p.category === category);
  };

  /** Get unique categories */
  const categories = Array.from(new Set(parameters.map((p) => p.category)));

  return {
    parameters,
    loading,
    saving,
    categories,
    saveParameters,
    addParameter,
    removeParameter,
    getByCategory,
    refetch: fetchParameters,
  };
}
